// FONT SERVICE CONSTANTS
// =============================================================================
export const FONT_CDN_ROOT =
  "https://cdn.us-east-1.prod.moon.dubai.aws.dev/AoQtxP83g5K6/jjT7qv4oGvoy/1/";
export const FONT_FAMILY_ATTRIBUTE = "data-font-family";
export const FONT_NAME_MAP = {
  "amazon-ember": "Amazon Ember",
  "fira-sans": "Fira Sans",
  inter: "Inter",
  "source-serif-pro": "Source Serif Pro",
} as const;

// MEDIA SERVICE CONSTANTS
// =============================================================================
export const MEDIA_CENTRAL = {
  baseUrl: "https://m.media-amazon.com/images/I/",
  defaultExtension: "png",
  certificationIconWidth: 36, // Default width for certification icons
  antiBlurrinessScaleFactor: 4, // Scale factor to prevent blurriness
} as const;

// BADGE SIZING CONSTANTS
// =============================================================================
export const BADGE_SIZING = {
  baseIconSize: 20, // Base icon size for medium badge (20px x 20px)
  baseFontSize: 14, // Base font size for medium badge text (14px)
  baseChevronWidth: 12, // Base chevron width for medium badge
  baseChevronHeight: 8, // Base chevron height for medium badge
  scaleFactors: {
    "extra-small": 0.5,
    small: 0.75,
    medium: 1.0,
    large: 1.25,
    "extra-large": 1.5,
  },
} as const;

// SHEET STYLING CONSTANTS
// =============================================================================
// These styles are applied to light DOM elements (via Portal) to ensure
// proper z-index stacking above external site elements
export const SHEET_STYLING = {
  zIndex: {
    backdrop: 2147483646, // Max z-index - 1 for backdrop
    sheet: 2147483647, // Max safe z-index value for sheet
  },
  transitions: {
    duration: "300ms",
    easing: "ease-out",
  },
  backdrop: {
    backgroundColor: "rgba(0, 0, 0, 0.7)",
  },
} as const;

// BUILD/BUNDLER CONSTANTS
// =============================================================================
export const ENTRY_CLIENT_PATH =
  "src/elements/amazon-sustainability-features/v1.0/entry-client.tsx";

// SUSTAINABILITY ELEMENT CONSTANTS
// =============================================================================
export const SUSTAINABILITY_ELEMENT = {
  tagName: "amazon-sustainability-features",
} as const;

export const CPF_LEARN_MORE_REF = "cpf_ext_lm";
export const CPF_LEARN_MORE_URL = "https://climatepledgefriendly.com/";

/**
 * Disclosure sheet header description.
 * Intentionally overrides the `description` field returned by the Titania
 * sustainability_features signal.
 */
export const CPF_DISCLOSURE_DESCRIPTION =
  "This product has sustainability features verified by trusted certifications.";

export const CUSTOMIZATION_SETTINGS = {
  fontFamily: "font-family",
  colorMode: "color-mode",
  badgeSize: "badge-size",
  disclosureFontSize: "disclosure-font-size",
  badgeTitle: "badge-title",
} as const;

export const PRODUCT_ATTRIBUTES = {
  productSku: "product-sku",
  productExternalId: "product-external-id",
  mockRoa: "mock-roa",
  useMockCpfData: "use-mock-cpf-data",
} as const;

export const ALL_ELEMENT_ATTRIBUTES = {
  ...CUSTOMIZATION_SETTINGS,
  ...PRODUCT_ATTRIBUTES,
} as const;

export const CUSTOMIZATION_DEFAULTS = {
  fontFamily: "amazon-ember" as const,
  colorMode: "light" as const,
  badgeSize: "medium" as const,
  disclosureFontSize: "medium" as const,
  badgeTitle: "feature-forward" as const,
} as const;

export const BADGE_TITLE_OPTIONS = {
  featureForward: "feature-forward",
  featureCount: "feature-count",
} as const;

// CSS FILE/CLASS MANAGEMENT
// =============================================================================
export const SUSTAINABILITY_CSS_PATH = "styles/sustainability" as const;

export const SUSTAINABILITY_SELECTORS = {
  initialPropsScript: "script#initial-props",
  root: "#root",
} as const;

export const SUSTAINABILITY_CSS_CLASSES = {
  themePrefix: "sustainability-theme-",
  fontFamilyProperty: "--sustainability-font-family",
  stylesDataAttribute: "data-sustainability-styles",
} as const;

export const DEFAULT_PRODUCT_ID = "UNKNOWN_PRODUCT" as const;

// METRICS CONSTANTS
// =============================================================================
export const METRICS_NAMESPACE = "Website/CPF Core/Climate Pledge Friendly";

export const METRICS_CTI = {
  category: "Website",
  type: "CPF Core",
  item: "Climate Pledge Friendly",
} as const;

export const CLICK_TYPE_TO_FEATURE_MAP = {
  bg: "badge",
  ct: "cert",
  lg: "logo",
  eo: "expander",
  lm: "learnmore",
} as const;

// TITANIA SERVICE CONSTANTS
// =============================================================================
export const ROA_INSTANCE_ID = "sustainability-adhoc-test";
export const SUSTAINABILITY_FEATURES_SIGNAL_ID = "sustainability_features";
export const PRODUCT_SIGNAL_TYPES = [SUSTAINABILITY_FEATURES_SIGNAL_ID];

export const TitaniaMetrics = {
  REQUEST_COUNT: "TITANIA_REQUEST_COUNT",
  REQUEST_DURATION: "TITANIA_REQUEST_DURATION",
  FAILED_REQUEST_COUNT: "TITANIA_FAILED_REQUEST_COUNT",
  PARSE_ERROR_COUNT: "TITANIA_PARSE_ERROR_COUNT",
  TIMEOUT_COUNT: "TITANIA_TIMEOUT_COUNT",
} as const;

export const ElementMetrics = {
  SERVER_SIDE_REQUEST_DURATION: "SERVER_SIDE_REQUEST_DURATION",
} as const;

// ELEMENT ROLE CONSTANTS
// =============================================================================
export const SUSTAINABILITY_FEATURES_ELEMENTS_EXECUTION_ROLE =
  "SustainabilityFeaturesElementsExecutionRole";

// CORS CONSTANTS
// =============================================================================
/**
 * Domains allowed for CORS requests beyond those registered via MCUI/Domain Service
 */
export const CORS_ALLOWED_DOMAINS = new Set([
  "console.buywithprime.amazon.com",
  "localhost",
  "*.myshopify.com",
  "*.amazon.com",
]);
